import { ChangeDetectorRef, Component, ElementRef, HostBinding, NgZone, OnInit, ViewChild } from '@angular/core';
import { Collections, HomeTabs, Results, Search } from '@local/client-contracts';
import { isLiveCollection, isWikiCollection } from '@local/common-web';
import { PopupService } from '@local/ui-infra';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { isEmpty, isEqual } from 'lodash';
import { map, Subscription } from 'rxjs';
import { WorkspacesService } from 'src/app/bar/services';
import { CollectionsSearchHelperService } from 'src/app/bar/services/collections-search-helper.service';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { ContextMenu } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { SearchOptions, SearchResultContext, SearchService } from 'src/app/bar/services/search';
import { LinkResourcesResultExtra, LinkResourcesSourceSettings, SourceSettings } from 'src/app/bar/services/search/client';
import { isLinkResourcesSettings } from 'src/app/bar/services/search/client/source-setings.util';
import { ScrollTrigger } from '../../../results';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { WidgetTitleComponent } from '../../components/widget-title/widget-title.component';
import { VerificationsCardsCommandsService } from '../../../collections-page/services/verifications-cards-commands-service';

@Component({
  selector: 'collection-widget',
  templateUrl: './collection-widget.component.html',
  styleUrls: ['./collection-widget.component.scss'],
})
export class CollectionWidgetComponent extends WidgetBaseComponent<HomeTabs.CollectionWidget> implements OnInit {
  private searchSubscription: Subscription;
  private lastPageInfo: { startIndex: number; endIndex: number; last: boolean };
  private nextPageRunning: boolean;
  private smallWidget: boolean;
  private readonly MAX_SMALL_WIDTH = 400;
  private readonly ACTIONS_MARGIN = 24;
  emptyStateModel = {
    title: 'This collection is empty',
    imgModel: { lightUrl: 'widget-collection-empty-light', darkUrl: 'widget-collection-empty-dark' },
  };

  searchId: string;
  collection: Collections.Collection = null;
  contextMenu: ContextMenu;
  sortPopupOpened: boolean;
  resultsCount: number;
  accessCollection: boolean;
  loaded = false;

  @ViewChild(WidgetTitleComponent) widgetTitleRef: WidgetTitleComponent;

  private getLiveSourceSettings(
    collection: Collections.Collection,
    searchParams?: Collections.LiveSearchParams
  ): LinkResourcesSourceSettings[] {
    const liveCollection: Collections.LiveCollection = collection as Collections.LiveCollection;
    return [
      {
        id: 'link-resources',
        type: 'link-resources',
        requestMaxCount: undefined,
        sorting: searchParams?.sort || liveCollection.searchParams?.sort,
        caching: { strategy: 'cache-and-source' },
        lastSyncTimes: true,
        noHeader: true,
        disableCloudGroup: true,
        useSourceFilters: true,
        filters: { preFilters: liveCollection.searchParams?.filters || {}, postFilters: {} },
        advancedSearch: true,
        contentSearch: true,
        tag: 'widget-live-collection',
      } as LinkResourcesSourceSettings,
    ];
  }

  private get isShareCollection(): boolean {
    return this.collection.isShared && this.isOwnerOrAdmin;
  }

  protected get itemHeights() {
    return null;
  }

  @HostBinding('attr.smallWidget')
  get isSmallWidget() {
    return this.smallWidget;
  }

  @HostBinding('style.--buttonsWidth') get buttonsWidth(): string {
    return `${this.widgetTitleRef?.getActionRefWidth() + this.ACTIONS_MARGIN}px`;
  }

  constructor(
    protected elementRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected ngZone: NgZone,
    protected session: SessionService,
    protected resultCommandService: ResultCommandService,
    private searchService: SearchService,
    public collectionsService: CollectionsService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected eventsService: EventsService,
    protected routerService: RouterService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService,
    private verificationsCardsCommandsService: VerificationsCardsCommandsService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.widgetResizeChange$.pipe(untilDestroyed(this)).subscribe((width) => {
      this.smallWidget = width < this.MAX_SMALL_WIDTH;
      this.cdr.markForCheck();
    });
    this.pageType = 'collection';
  }

  async openContextMenu(event: MouseEvent) {
    this.contextMenu = { event, source: 'actions', itemId: this.collection.id };
  }

  async loadData() {
    this.collectionsService.all$
      .pipe(
        untilDestroyed(this),
        map((c) => c?.find((c) => c.id === this.settings?.collectionId))
      )
      .subscribe(async (collection) => this.refreshData(collection));
  }

  async refreshData(collection: Collections.Collection, force?: boolean) {
    if (!collection) {
      this.accessCollection = false;
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }
    if (isEqual(this.collection, collection) && !force) {
      return;
    }
    this.loaded = true;
    this.accessCollection = true;
    this.collection = collection;
    if (isWikiCollection(collection)) {
      this.emptyStateModel = {
        title: 'This wiki is empty',
        imgModel: { lightUrl: 'widget-collection-empty-light', darkUrl: 'widget-collection-empty-dark' },
      };
    }
    this.searchSession = this.searchService.getOrCreateSearchSession(`collections${this.collection?.id}`);
    this.cdr.markForCheck();
    this.search(
      CollectionsSearchHelperService.getSourceSettings(this.collection, this.getLiveSourceSettings, { sort: this.currentSort }, true)
    );
  }

  prepareNextPage(trigger: ScrollTrigger): void {
    if (this.nextPageRunning || !this.displayedContext.searchCompleted) return;
    this.nextPageRunning = true;
    const sources = this.displayedContext.sources
      .filter((s) => s.source.type === 'link-resources')
      .map((s) => ({ id: s.source?.id, type: s.source?.type }));

    this.searchSession.nextPage$(trigger, sources).subscribe({
      next: (ctxs) => {
        if (!ctxs) {
          return;
        }
        const resourceSource = this.displayedContext.sources?.find((s) => s.source.type === 'link-resources');
        const extra = resourceSource?.extra;
        this.lastPageInfo = extra?.lastPageInfo;
        this.searchId = extra?.searchId;
      },
      complete: () => {
        this.nextPageRunning = false;
      },
    });
  }

  private search(sources: SourceSettings[]) {
    this.nextPageRunning = false;
    this.isLoading = true;
    this.cdr.markForCheck();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = null;
    }
    const options: SearchOptions = {
      resetSession: false,
      sources: sources,
      trigger: 'collection_widget_load',
    };
    if (isLiveCollection(this.collection)) {
      const query = this.collection.searchParams?.query;
      if (!query && isEmpty(this.collection.searchParams?.filters)) {
        this.isLoading = false;
        this.resultsCount = 0;
        this.displayedContext = { items: [], id: -1, clientSearchId: '', sessionId: '' };
        return;
      }
      options.query = query;
    }

    this.searchSubscription = this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx: SearchResultContext) => {
        if (ctx.searchCompleted) {
          const resourceSource = ctx.sources?.find((s) => isLinkResourcesSettings(s.source));
          const extra = resourceSource?.extra as LinkResourcesResultExtra;
          const items = ctx.items
            .map((item) => {
              const showContextMenu = item.type !== 'collection-header';
              const itemAddedTime = (<Search.ResultItem>item).showResultSections?.showNewIndication;
              const showResultSections = {
                showTags: false,
                showBullets: false,
                showSubtitleInSecondLine: true,
                showSubtitleInEndLine: item.source === 'wiki-collection-items' ? true : false,
                showContextMenu,
                showPreviewIcon: false,
                showSummaryIcon: false,
                showPreviewIconContextMenu: !isWikiCollection(this.collection),
                showNewIndication: itemAddedTime,
              };
              return {
                ...item,
                showResultSections,
              };
            })
            .filter((item) => item.type !== 'header');
          this.displayedContext = { ...ctx, items };
          this.lastPageInfo = extra?.lastPageInfo;
          this.searchId = extra?.searchId;
          this.resultsCount = CollectionsSearchHelperService.getCollectionResultsCount(this.collection, ctx);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    const eventInfo = { ...data.eventInfo, resources: [{ list: 'collection' }] };
    data = { ...data, eventInfo };
    this.resultCommandService.onInvoke(
      this.displayedContext.items,
      data,
      type,
      'collection',
      'collection',
      'collection',
      this.selectedIndex,
      0
    );
  }

  layoutChanged(layout: Results.LayoutType) {
    this.updateSettingWidget({ ...this.settings, layout } as HomeTabs.CollectionWidget, { layout: true }, this.isShareCollection);
  }

  updateSort(searchSort?: Search.Sort) {
    const updatedWidget = { ...this.settings, sort: searchSort } as HomeTabs.CollectionWidget;
    const changed = this.initSort(updatedWidget);
    if (!changed || !this.collection) {
      return;
    }
    this.refreshData(this.collection, true);
    this.updateSettingWidget(updatedWidget, { sort: true }, this.isShareCollection);
  }

  onClickCheckbox(event: PointerEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.saveStateImmediate = !this.saveStateImmediate;
  }

  tryAgain() {
    this.loadData();
  }

  onDynamicCommand($event: { value: string; item: Search.WikiCardCollectionResultItem }) {
    const commandType: string = $event.value;
    if (this.verificationsCardsCommandsService.isVerificationCommand(commandType)) {
      this.verificationsCardsCommandsService.executeCommand(commandType, $event.item);
    }
  }
}
